import {ThemeProvider} from "@mui/material";
import {
    createContext,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState
} from "react";
import {createAppTheme} from "./theme";

export const APPEARANCE_KEY = "esfinge.aparencia";
export const APPEARANCE_OPTIONS = [
    {value: "system", label: "Automático", icon: "brightness_auto"},
    {value: "light", label: "Claro", icon: "light_mode"},
    {value: "dark", label: "Escuro", icon: "dark_mode"}
];
const validPreference = (value) =>
    APPEARANCE_OPTIONS.some((option) => option.value === value)
        ? value
        : "system";
function readPreference() {
    try {
        return validPreference(window.localStorage.getItem(APPEARANCE_KEY));
    } catch {
        return "system";
    }
}
function useMedia(query) {
    const media = useMemo(() => window.matchMedia?.(query), [query]);
    const [matches, setMatches] = useState(Boolean(media?.matches));
    useEffect(() => {
        if (!media) return;
        const listener = (event) => setMatches(event.matches);
        setMatches(media.matches);
        if (media.addEventListener) media.addEventListener("change", listener);
        else media.addListener(listener);
        return () => {
            if (media.removeEventListener)
                media.removeEventListener("change", listener);
            else media.removeListener(listener);
        };
    }, [media]);
    return matches;
}
const AppearanceContext = createContext({
    preference: "system",
    mode: "light",
    setPreference: () => {}
});
export const useAppearance = () => useContext(AppearanceContext);

export default function AppearanceProvider({children}) {
    const [preference, setStoredPreference] = useState(readPreference);
    const systemDark = useMedia("(prefers-color-scheme: dark)");
    const printMedia = useMedia("print");
    const [printing, setPrinting] = useState(false);
    const mode =
        printing || printMedia
            ? "light"
            : preference === "system"
            ? systemDark
                ? "dark"
                : "light"
            : preference;
    const theme = useMemo(() => createAppTheme(mode), [mode]);
    useLayoutEffect(() => {
        document.documentElement.dataset.appearance = preference;
        document.documentElement.dataset.colorMode = mode;
        document.documentElement.style.colorScheme = mode;
        document.documentElement.style.backgroundColor =
            theme.palette.background.default;
    }, [preference, mode, theme]);
    useEffect(() => {
        const onStorage = (event) => {
            if (event.key === APPEARANCE_KEY || event.key === null)
                setStoredPreference(readPreference());
        };
        const beforePrint = () => setPrinting(true);
        const afterPrint = () => setPrinting(false);
        window.addEventListener("storage", onStorage);
        window.addEventListener("beforeprint", beforePrint);
        window.addEventListener("afterprint", afterPrint);
        return () => {
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("beforeprint", beforePrint);
            window.removeEventListener("afterprint", afterPrint);
        };
    }, []);
    function setPreference(value) {
        const next = validPreference(value);
        setStoredPreference(next);
        try {
            window.localStorage.setItem(APPEARANCE_KEY, next);
        } catch {
            /* A seleção continua válida nesta aba sem armazenamento. */
        }
    }
    return (
        <AppearanceContext.Provider value={{preference, mode, setPreference}}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </AppearanceContext.Provider>
    );
}
